import { useEffect, useRef } from 'react'

const AUDIO_SRC = '/audio/Looping_Sea.aac'
const FADE_DURATION = 1.5   // seconds — user-initiated mute/unmute
const HIDE_FADE_DURATION = 0.3 // seconds — tab hide/show (faster)
const TARGET_GAIN = 0.631   // -4 dB (10^(-4/20))

export default function AudioManager({ muted, bannerDismissed }) {
  const audioRef = useRef(null)
  const ctxRef = useRef(null)
  const gainRef = useRef(null)
  const startedRef = useRef(false)
  // Keep a ref to the latest muted value so visibility handlers don't close over stale state
  const mutedRef = useRef(muted)
  // Keep a ref to the latest bannerDismissed value so the start handler reads current state
  const bannerDismissedRef = useRef(bannerDismissed)
  // A device is treated as "mobile" (requires gesture + banner dismiss) only when
  // the primary pointer is coarse (finger). Desktops and laptops have a fine pointer
  // (mouse) even if they also support touch — this avoids false-positive mobile
  // detection from ontouchstart being present in desktop Chrome on Windows.
  const isMobile = window.matchMedia('(pointer: coarse)').matches

  useEffect(() => {
    mutedRef.current = muted
  }, [muted])

  useEffect(() => {
    bannerDismissedRef.current = bannerDismissed
  }, [bannerDismissed])

  // Initialise audio on first user gesture.
  // Desktop: any gesture triggers start immediately.
  // Mobile: gesture is ignored until the welcome banner has been dismissed.
  useEffect(() => {
    const removeListeners = () => {
      document.removeEventListener('click', onGesture)
      document.removeEventListener('pointerdown', onGesture)
      document.removeEventListener('touchstart', onGesture)
      document.removeEventListener('keydown', onGesture)
    }

    const doStart = () => {
      const audio = new Audio(AUDIO_SRC)
      audio.loop = true
      audio.preload = 'auto'
      audio.setAttribute('playsinline', '')
      audioRef.current = audio

      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      ctxRef.current = ctx

      const gain = ctx.createGain()
      gain.gain.setValueAtTime(0, ctx.currentTime)
      if (!mutedRef.current) {
        gain.gain.linearRampToValueAtTime(TARGET_GAIN, ctx.currentTime + FADE_DURATION)
      }
      gain.connect(ctx.destination)
      gainRef.current = gain

      const source = ctx.createMediaElementSource(audio)
      source.connect(gain)

      ctx.resume().then(() => {
        audio.play().catch(() => {})
      })
    }

    const onGesture = () => {
      if (startedRef.current) return
      // On mobile, wait until the welcome banner has been dismissed
      if (isMobile && !bannerDismissedRef.current) return
      startedRef.current = true
      doStart()
      removeListeners()
    }

    document.addEventListener('click', onGesture)
    document.addEventListener('pointerdown', onGesture)
    document.addEventListener('touchstart', onGesture, { passive: true })
    document.addEventListener('keydown', onGesture)

    return () => removeListeners()
  }, [])

  // Tab visibility / app background handling
  // Uses both visibilitychange (all modern browsers) and pagehide (iOS Safari)
  useEffect(() => {
    const fadeGain = (targetValue, duration) => {
      const gain = gainRef.current
      const ctx = ctxRef.current
      if (!gain || !ctx) return
      const now = ctx.currentTime
      gain.gain.cancelScheduledValues(now)
      gain.gain.setValueAtTime(gain.gain.value, now)
      gain.gain.linearRampToValueAtTime(targetValue, now + duration)
    }

    const handleHide = () => {
      const gain = gainRef.current
      const ctx = ctxRef.current
      if (!gain || !ctx) return
      // Fade out quickly, then suspend the context so iOS doesn't kill it
      fadeGain(0, HIDE_FADE_DURATION)
      setTimeout(() => {
        if (ctx.state === 'running') ctx.suspend()
      }, HIDE_FADE_DURATION * 1000 + 50)
    }

    const handleShow = () => {
      const gain = gainRef.current
      const ctx = ctxRef.current
      const audio = audioRef.current
      if (!gain || !ctx || !audio) return
      // Resume context first (iOS requires this after suspension)
      ctx.resume().then(() => {
        // Ensure audio element is still playing (iOS may have paused it)
        if (audio.paused) audio.play().catch(() => {})
        // Only fade back in if user hasn't manually muted
        if (!mutedRef.current) {
          fadeGain(TARGET_GAIN, HIDE_FADE_DURATION)
        }
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleHide()
      } else {
        handleShow()
      }
    }

    // pagehide fires on iOS Safari when the user switches apps or tabs
    const handlePageHide = () => handleHide()
    // pageshow fires on iOS Safari when returning
    const handlePageShow = () => {
      // Small delay — iOS needs a moment before AudioContext.resume() works
      setTimeout(handleShow, 300)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  // Smooth mute/unmute via gain ramp (user-initiated)
  useEffect(() => {
    const gain = gainRef.current
    const ctx = ctxRef.current
    if (!gain || !ctx) return
    const now = ctx.currentTime
    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(gain.gain.value, now)
    gain.gain.linearRampToValueAtTime(muted ? 0 : TARGET_GAIN, now + FADE_DURATION)
  }, [muted])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.pause()
      ctxRef.current?.close()
    }
  }, [])

  return null
}
